import NewReviewCard from '@/components/elements/Card/ReviewCard';
import { Button } from '@/components/elements/forms/buttons';
import { LoadingIcon } from '@/components/icons';
import { ReviewStats } from '@/components/modules/review';
import Employee from '@/components/modules/salon/Employee';
import EmptyState from '@/components/templates/EmptyState';
import { getAuthorInitials } from '@/features/ProfilePages/helpers/review';
import { capitalizeFirstLetter, server } from '@/helpers';
import useBokadirektPayBadges from '@/hooks/useBokadirektPayBadges';
import { _t } from '@/locale';
import { useEffect, useRef, useState } from 'react';
import { LegacyModalHeader } from '../mobile';
import { ModalContent, ModalDialog } from '../modals';
import { ModalActionbar } from '../modals/Modal';

function LegacyEmployeeReviews(props) {
  const { isOpen, close, employee, place, rating = {}, pickService, showMoreInfo, isMpReviews } = props;
  const { score, count, ratingCounts, reviewCounts } = rating;
  const stats = { score, count };
  const { about: { settings: { subscriptionType } = {} } = {} } = place;
  const { about: { settings: { displayReviews } = {} } = {} } = employee;
  const [reviews, setReviews] = useState([]);
  const [nextReviewPageData, setNextReviewPageData] = useState({});
  const [haveMoreReviews, setHaveMoreReviews] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [ratingFilter, setRatingFilter] = useState(0);
  const tooltipTargetRef = useRef(null);
  const bokadirektPayBadges = useBokadirektPayBadges(place, employee);

  useEffect(() => {
    if (displayReviews && typeof rating?.score === 'number') {
      fetchReviews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratingFilter]);

  function fetchReviewsByRating(rating = 0) {
    setRatingFilter(rating);
    if (rating !== ratingFilter) {
      setReviews([]);
      setNextReviewPageData({});
      setHaveMoreReviews(true);
    }
  }

  async function fetchReviews() {
    if (!haveMoreReviews) {
      return;
    }
    setIsLoading(true);
    const result = await server.request
      .get('/reviews/v2/list', {
        params: {
          placeId: place.id,
          employeeId: employee.id,
          page: nextReviewPageData.page,
          limit: nextReviewPageData.limit,
          isMpReviews,
          hasMessage: true,
          rating: ratingFilter,
          timeFilter: 'forever',
        },
      })
      .then(server.handleSuccess)
      .catch(server.handleError);

    setReviews(reviews.concat(result.items));
    setNextReviewPageData(result.nextPage || {});
    setHaveMoreReviews(
      result.nextPage && result.nextPage && result.nextPage.limit && result.nextPage.limit > 0 ? true : false,
    );
    setIsLoading(false);
  }

  const bookClicked = (e) => {
    pickService();
  };

  return (
    <ModalDialog isOpen={isOpen} appElement={document.getElementById('root')} onRequestClose={close}>
      <ModalContent size="md">
        <LegacyModalHeader
          action={close}
          cancel
          top={
            <h3 className="font-semibold sm:hidden">
              {(displayReviews ? _t('reviewsFor') + ' ' : '') + capitalizeFirstLetter(employee.about.name)}
            </h3>
          }
        />

        <div className="container bg-white py-6 sm:bg-transparent">
          <Employee
            employee={employee}
            place={place}
            rating={rating}
            inModal
            showMoreInfo={showMoreInfo}
            bokadirektPayBadges={bokadirektPayBadges}
          />
          {!Boolean(reviews.length) && (
            <div className="gap-2xl flex flex-col">
              <EmptyState title={_t('notEnoughReviews')} icon={<img src="/images/illustrations/empty.svg" alt="" />} />
            </div>
          )}
          {reviews.length > 0 && <h2 className="text-lg font-semibold">{_t('reviewsTitle')}</h2>}
          {displayReviews && (
            <ReviewStats
              stats={stats}
              ratingCounts={ratingCounts}
              reviewCounts={reviewCounts}
              onRatingClick={fetchReviewsByRating}
              ratingFilter={ratingFilter}
              showScore={false}
            />
          )}
          <div className="flex flex-row flex-wrap items-stretch justify-between gap-4 pb-16 sm:pb-4">
            {reviews.map((review, key) => {
              const initials = getAuthorInitials(review.author.name);
              return (
                <NewReviewCard
                  tooltipTargetRef={tooltipTargetRef}
                  showVerified={false}
                  verified={true}
                  key={key}
                  placeId={place.id.toString()}
                  reviewId={review.id}
                  companyReply={
                    review.reply && subscriptionType !== 'essential' && subscriptionType !== 'mini'
                      ? { ...review.reply, author: review.reply.author.name }
                      : undefined
                  }
                  review={{
                    ...review.review,
                    initials,
                    author: review.author.name,
                    createdAt: review.createdAt,
                    employee: review.employee?.name ? review.employee.name : undefined,
                  }}
                />
              );
            })}

            {isLoading && (
              <div className="flex w-full justify-center">
                <LoadingIcon className="h-8 w-8" />
              </div>
            )}
            {reviews.length > 0 && haveMoreReviews && (
              <Button variant="secondary" className="mx-auto px-4" onClick={fetchReviews}>
                {_t('showMore')}
              </Button>
            )}
          </div>
          <div className="hidden sm:block">
            {pickService && (
              <Button block onClick={bookClicked}>
                {_t('book')}
              </Button>
            )}
          </div>
        </div>
        <div className="sm:hidden">
          {pickService && (
            <ModalActionbar>
              <Button block onClick={bookClicked}>
                {_t('book')}
              </Button>
            </ModalActionbar>
          )}
        </div>
      </ModalContent>
    </ModalDialog>
  );
}

export default LegacyEmployeeReviews;
